import type { GroupItem } from "@/features/map-markers/device-marker/components/group-menu/types";
import type { WARNING_STATUS } from "@/features/map-markers/device-marker/configs/variants";
import type MapEntityBase from "@/services/map-management/map-entities/map-entity-base";

import type { MarkerAppearance } from "./components/marker-appearances";

/** Everything clustering and viewport culling need */
export type MapPoint = {
    id: string;
    lat: number;
    long: number;
};

/** Whether a DTO's coordinates can be drawn at all — legacy documents can carry null ones */
export const hasUsablePosition = ({ lat, long }: { lat: unknown; long: unknown }) =>
    Number.isFinite(lat) && Number.isFinite(long);

/** Which cached list produced the marker — one v2 list query. `README.md` covers why not `model.category`. */
export type MarkerSource = "device" | "thirdPartyDevice" | "sensoterraDevice";

/** Namespaces a marker's id by source, so two lists sharing a model id cannot collide */
export const markerIdFor = (source: MarkerSource, modelId: string) => `${source}:${modelId}`;

/** How far a marker's relevance reaches, and which selections apply to it */
export type MarkerScope = { covers: "site" } | { covers: "zones"; zoneIds: readonly string[] };

/** What every marker carries, whichever layer ends up drawing it */
type MarkerBase = MapPoint & {
    source: MarkerSource;
    /** The id within this source's own domain, and the key the entity index is under */
    modelId: string;
    /** Required on both arms, so a migrating source cannot silently opt out of selection scoping */
    scope: MarkerScope;
};

/**
 * A marker on the map, carrying everything needed to draw it and nothing else behind it.
 *
 * Flat, so a marker is a `MapPoint` and clustering consumes one with nothing to unpack.
 */
export type MapMarker = MarkerBase & {
    /**
     * Which layer draws this marker — two generations, so both values name one. Transitional, and
     * retires with `LegacyDrawnMarker` below: once every marker is one of these the tag has nothing
     * left to distinguish
     */
    drawnBy: "v2";
    /** Names a row in `components/marker-appearances.ts`, resolved to graphics at draw time */
    appearance: MarkerAppearance;
    /** Device state rather than appearance, so it sits on the marker: `resolveMarkerWarning` decides it */
    badge: { variant: "error" | "warning" } | null;
    tooltip: { label: string; warningText?: string };
    onClick: () => void;
};

/** A marker the legacy icon layer draws, reading its appearance off the MapEntity */
export type LegacyDrawnMarker = MarkerBase & {
    drawnBy: "legacy";
    mapEntity: MapEntityBase;
};

/** Whether the legacy renderer owns this marker, rather than the map drawing it directly */
export const isLegacyDrawn = (marker: MapMarker | LegacyDrawnMarker): marker is LegacyDrawnMarker =>
    marker.drawnBy === "legacy";

/** One row in a group's menu, supplied by whichever layer owns that member */
export type GroupMemberRow = GroupItem & {
    /** Feeds the group's badge, which shows the highest-priority warning among its members */
    warningStatus?: WARNING_STATUS;
    /** The group ring is binary, so one boolean stands in for the whole attribute pipeline */
    hasProgressRing: boolean;
};

export type GroupMemberRowsById = ReadonlyMap<string, GroupMemberRow>;
